// Profil de pêche composé : espèce + poste + esche (poste et esche facultatifs), règles des trois
// en ET. L'espèce donne la saison, le délai, le seuil `soft` et la catégorie ; poste et esche
// ajoutent leurs règles (toutes `hard`) et leurs conditions de créneau, dont les valeurs peuvent
// être remplacées par les paramètres du profil (limites de son bateau, écart au crépuscule).
// Une règle d'espèce à `when_poste` n'est gardée qu'avec l'un de ces postes (espèce seule comprise).
import type { ResolvedTemplate } from './resolve.ts';
import type { HourlyCondition, Rule } from './schema.ts';

/** Paramètres d'un profil de tableau (`table_profiles.params`), ex. `{ "vent_max": 12 }`. */
export type ProfileParams = Record<string, number>;

export interface ProfileParts {
  species: ResolvedTemplate;
  poste?: ResolvedTemplate | null;
  esche?: ResolvedTemplate | null;
}

/** Nom affiché d'un profil composé : « Bar · leurre · bord » (esche puis poste). */
export function profileName(species: string, esche: string | null, poste: string | null): string {
  return [species, ...[esche, poste].flatMap((part) => (part ? [part.toLocaleLowerCase('fr-FR')] : []))].join(' · ');
}

/** Slug d'un profil composé : `bar+leurre+bord`, `bar+kayak`, `bar`. */
export function profileSlug(species: string, esche: string | null, poste: string | null): string {
  return [species, esche, poste].filter((part) => part !== null).join('+');
}

/**
 * Fusion : conditions `hourly` du poste puis de l'esche, `anchor` de l'esche, `min_hours` le plus
 * long, `hour_range` = intersection (vide : aucun créneau, refusée à l'enregistrement d'un
 * tableau), `navigation` du poste. Une limite d'un nom donné remplace la valeur de toutes les
 * conditions qui portent ce nom, poste et esche confondus.
 */
export function composeProfile({ species, poste = null, esche = null }: ProfileParts, params: ProfileParams = {}): ResolvedTemplate {
  const speciesRules = forPoste(species.rules, poste?.slug ?? null);
  if (!poste && !esche) return { ...species, rules: speciesRules };
  const withParams = (condition: HourlyCondition): HourlyCondition => {
    const value = condition.param === undefined ? undefined : params[condition.param];
    return typeof value === 'number' && Number.isFinite(value) ? { ...condition, value } : condition;
  };
  const parts = [poste, esche].filter((part) => part !== null);
  const minHours = parts.flatMap((part) => (part.min_hours === null ? [] : [part.min_hours]));
  return {
    ...species,
    slug: profileSlug(species.slug, esche?.slug ?? null, poste?.slug ?? null),
    name: profileName(species.name, esche?.name ?? null, poste?.name ?? null),
    rules: [...speciesRules, ...parts.flatMap((part) => part.rules)],
    hourly: parts.flatMap((part) => part.hourly).map(withParams),
    anchor: (esche?.anchor ?? []).map(withParams),
    min_hours: minHours.length > 0 ? Math.max(...minHours) : null,
    hour_range: [Math.max(...parts.map((part) => part.hour_range[0])), Math.min(...parts.map((part) => part.hour_range[1]))],
    navigation: poste?.navigation ?? false,
  };
}

/** Règles d'espèce qui valent pour ce poste (`when_poste` absent ou qui le cite), sans `when_poste`. */
function forPoste(rules: readonly Rule[], poste: string | null): Rule[] {
  return rules.flatMap(({ when_poste: postes, ...rule }) => (!postes || (poste !== null && postes.includes(poste)) ? [rule] : []));
}
